import { Component } from 'react';
import { Contribution } from '../../../../../../../common/model/input/contributions/types';
import { CommerceType } from '../../../../../../../common/model/input/products/types';
import { ModelEntityType } from '../../../../../../../common/model/input/types';
import { ModelEntitySearchResponseItem } from '../../../../../../../common/model/search/types';
import { ModalOptions } from '../../../../../../untidy/redux/modules/modal';
import { aiName } from '../../../../../../untidy/texts';
import { TextField } from '../../../../../generic/components/inputs/text-field';
import { themeDEPRECATED } from '../../../../../generic/global/styles';
import Search from '../../../output/search';
import {
  ContributionModalParagraph,
  ContributionModalTitle,
  FormRow,
  FullRow,
  getContributeSearchStyle,
  Label,
  ProductCodeWithLabel,
  ProductRow,
  RemoveLink,
} from './common';
import { FormButton } from './form-button';

interface State {
  comment?: string;
  upstreamProducts?: ProductCodeWithLabel[];
  downstreamProducts?: ProductCodeWithLabel[];
}

interface Props {
  isSubmitting: boolean;
  submitContribution: (options: { target?: string; data: Contribution }) => void;
  options: ModalOptions;
}

export class ProductKnowledgeForm extends Component<Props, State> {
  public state: State = {};

  public render() {
    const {
      isSubmitting,
      options: { target, targetName, existingData },
    } = this.props;
    const { upstreamProducts, downstreamProducts, comment } = this.state;
    const commerceType = existingData && existingData.commerceType;

    if (!target || !targetName) {
      console.error('Missing required metadata for product');
      return null;
    }

    const canSubmit =
      (upstreamProducts && upstreamProducts.length > 0) || (downstreamProducts && downstreamProducts.length > 0);
    const showDownstreamQuestion = commerceType !== CommerceType.B2C;

    return (
      <>
        <ContributionModalTitle>
          Know something about {targetName ? targetName.toLowerCase() : 'this product'}? Great, please share!
        </ContributionModalTitle>
        <ContributionModalParagraph>
          The products and services in the Upright net impact model are linked to one another according to value chains.
          For example, a mobile phone doesn't just get impacts from the process of putting its components together, but
          it also inherits impacts from its entire supplychain.
        </ContributionModalParagraph>
        <form
          onSubmit={e => {
            e.preventDefault();
          }}
        >
          <ContributionModalParagraph>
            <strong>Upstream products</strong> mean products or services that are used to produce a particular product.
            Example: for an apple pie, upstream products might include apples, wheat flour and online baking tutorials.
          </ContributionModalParagraph>
          <FormRow style={{ marginTop: 0, marginBottom: themeDEPRECATED.rhythm(5) }}>
            <FullRow>
              <Label>What are the upstream products for {targetName.toLowerCase()}?</Label>
              {upstreamProducts &&
                upstreamProducts.map(productDatum => (
                  <ProductRow key={productDatum.productCode}>
                    {productDatum.label}{' '}
                    <RemoveLink
                      onClick={() => {
                        this.setState(state => ({
                          upstreamProducts:
                            state.upstreamProducts &&
                            state.upstreamProducts.filter(p => p.productCode !== productDatum.productCode),
                        }));
                      }}
                    >
                      (remove)
                    </RemoveLink>
                  </ProductRow>
                ))}
              <Search
                creatable="Suggest a new product:"
                onChange={(option?: ModelEntitySearchResponseItem) => {
                  if (!option) {
                    return;
                  }
                  this.setState(state => {
                    return {
                      upstreamProducts: (state.upstreamProducts || []).concat({
                        productCode: option.id,
                        userCreated: option.created,
                        label: option.name,
                      }),
                    };
                  });
                }}
                searchTypes={[ModelEntityType.PRODUCT, ModelEntityType.MIXIN]}
                styleOverride={getContributeSearchStyle()}
              />
            </FullRow>
          </FormRow>
          {showDownstreamQuestion && (
            <>
              <ContributionModalParagraph>
                <strong>Downstream products</strong> mean products or services that require this product when they are
                produced. Example: for an apple, downstream products might include an apple pie or perhaps wholesale of
                fruits.
              </ContributionModalParagraph>
              <FormRow style={{ marginTop: 0, marginBottom: themeDEPRECATED.rhythm(5) }}>
                <FullRow>
                  <Label>What are the downstream products for {targetName.toLowerCase()}?</Label>
                  {downstreamProducts &&
                    downstreamProducts.map(productDatum => (
                      <ProductRow key={productDatum.productCode}>
                        {productDatum.label}{' '}
                        <RemoveLink
                          onClick={() => {
                            this.setState(state => ({
                              downstreamProducts:
                                state.downstreamProducts &&
                                state.downstreamProducts.filter(p => p.productCode !== productDatum.productCode),
                            }));
                          }}
                        >
                          (remove)
                        </RemoveLink>
                      </ProductRow>
                    ))}
                  <Search
                    creatable="Suggest a new product:"
                    onChange={(option?: ModelEntitySearchResponseItem) => {
                      if (!option) {
                        return;
                      }
                      this.setState(state => {
                        return {
                          downstreamProducts: (state.downstreamProducts || []).concat({
                            productCode: option.id,
                            userCreated: option.created,
                            label: option.name,
                          }),
                        };
                      });
                    }}
                    searchTypes={[ModelEntityType.PRODUCT, ModelEntityType.MIXIN]}
                    styleOverride={getContributeSearchStyle()}
                  />
                </FullRow>
              </FormRow>
            </>
          )}
          <FormRow>
            <TextField
              label="Anything else you want to share?"
              id="comment"
              type="textarea"
              value={comment}
              disabled={isSubmitting}
              metaLabel="Optional"
              onChange={value => {
                this.setState({ comment: value });
              }}
            />
          </FormRow>
          <ContributionModalParagraph style={{ marginTop: themeDEPRECATED.rhythm(5) }}>
            Please note that contributions will not automatically affect the data in the model, but will be processed
            and evaluated either by {aiName} or the Upright team.
          </ContributionModalParagraph>
          <FormRow>
            <FormButton
              type="submit"
              disabled={isSubmitting || !canSubmit}
              onClick={() => {
                if (
                  (this.state.downstreamProducts && this.state.downstreamProducts.length > 0) ||
                  (this.state.upstreamProducts && this.state.upstreamProducts.length > 0)
                ) {
                  this.props.submitContribution({
                    data: {
                      type: 'PRODUCT_KNOWLEDGE',
                      comment: this.state.comment,
                      upstreamProducts: this.state.upstreamProducts
                        ? this.state.upstreamProducts.filter(p => !p.userCreated).map(p => p.productCode)
                        : [],
                      newUpstreamProducts: this.state.upstreamProducts
                        ? this.state.upstreamProducts.filter(p => p.userCreated).map(p => p.label)
                        : [],
                      downstreamProducts: this.state.downstreamProducts
                        ? this.state.downstreamProducts.filter(p => !p.userCreated).map(p => p.productCode)
                        : [],
                      newDownstreamProducts: this.state.downstreamProducts
                        ? this.state.downstreamProducts.filter(p => p.userCreated).map(p => p.label)
                        : [],
                    },
                    target,
                  });
                }
              }}
              style={{ width: '100%' }}
            >
              Submit
            </FormButton>
          </FormRow>
        </form>
      </>
    );
  }
}
